import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { get, set, del, type UseStore } from 'idb-keyval';
import { QueryKey } from '@tanstack/react-query';
import { withStore } from './store';

// If there are any queries that we always should refetch on load.
// They will still be cached in-memory but will not persist on reload
const excludedQueries: string[] = [
  'authenticated-user',
  'federated-current-user',
  'supplier-signup-response',
];

export const createIDBPersister = (idbValidKey: IDBValidKey = 'reactQuery') => {
  let persistDisabled = false;

  return {
    persistClient: async (client: PersistedClient) => {
      // `persistQueryClientSave` upstream discards this promise and has no try/catch, so a
      // rejection here becomes an unhandled rejection on every cache event. Never reject.
      if (persistDisabled) return;
      try {
        await withStore((usableStore: UseStore) => set(idbValidKey, client, usableStore));
      } catch (error) {
        // withStore already reopened once. Still failing means this environment cannot
        // persist at all (private mode, quota, blocked) — stop trying for this page.
        persistDisabled = true;
      }
    },
    restoreClient: async () => {
      try {
        return await withStore((usableStore: UseStore) =>
          get<PersistedClient>(idbValidKey, usableStore)
        );
      } catch (error) {
        // A missing store on the first-ever load is the NORMAL cold start, not an incident
        return undefined;
      }
    },
    removeClient: async () => {
      // Deliberately NOT gated on persistDisabled: bustCacheForIdentity relies on this to
      // delete a DIFFERENT user's cached data, so skipping it would be a data-leak-shaped bug.
      await withStore((usableStore: UseStore) => del(idbValidKey, usableStore));
    },
  } as Persister;
};

export const persister = createIDBPersister('delogue-idb');

export const persistOptions = {
  persister,
  maxAge: 1000 * 60 * 60 * 24, // Persisted data should never be older than 1 day
  buster: APP_VERSION, // Bust all cache when our version change
  dehydrateOptions: {
    // Never dehydrate these queries
    shouldDehydrateQuery: ({ queryKey }: { queryKey: QueryKey }) => {
      return !(typeof queryKey[0] === 'string' && excludedQueries.includes(queryKey[0]));
    },
  },
};
